import { useState } from "react";

import { downloadFile } from "@/core/utils/download";
import {
  baixarFichaAnexo,
  incluirFichaAnexos,
  listFichaAnexos,
  removerFichaAnexo,
} from "@/modules/financeiro-fichas";
import type { FichaAnexo, IncluirFichaAnexoPayload } from "@/modules/financeiro-fichas";
import { FICHAS_FORBIDDEN_MESSAGE, useFichasQuery } from "./use-fichas-financeiras";

export const ANEXOS_ERROR_MESSAGE = "Não foi possível carregar os anexos. Tente novamente.";
export const ANEXO_INCLUIDO_MESSAGE = "Arquivo anexado à ficha.";
export const ANEXO_INCLUIR_ERROR_MESSAGE = "Não foi possível anexar o arquivo. Tente novamente.";
export const ANEXO_REMOVIDO_MESSAGE = "Arquivo removido da ficha.";
export const ANEXO_REMOVER_ERROR_MESSAGE = "Não foi possível remover o arquivo. Tente novamente.";
export const ANEXO_ARQUIVO_ERROR_MESSAGE = "Não foi possível abrir o arquivo. Tente novamente.";

export type ResultadoAnexo = "ok" | "erro";

const MIME_PADRAO = "application/octet-stream";

/**
 * Anexos da ficha (HU 2688): relação, inclusão, remoção e arquivo. Depois de incluir ou remover
 * com sucesso a relação é recarregada. Sem `enabled`, nada é consultado.
 */
export function useAnexosFicha(obraId: string, fichaId: string, enabled = true) {
  const query = useFichasQuery<FichaAnexo[]>(
    (signal) => listFichaAnexos(obraId, fichaId, signal),
    ANEXOS_ERROR_MESSAGE,
    `anexos/${obraId}/${fichaId}`,
    { enabled, forbiddenMessage: FICHAS_FORBIDDEN_MESSAGE },
  );
  const [enviando, setEnviando] = useState(false);
  const [removendo, setRemovendo] = useState(false);

  async function incluir(payloads: IncluirFichaAnexoPayload[]): Promise<ResultadoAnexo> {
    setEnviando(true);
    try {
      await incluirFichaAnexos(obraId, fichaId, payloads);
      query.reload();
      return "ok";
    } catch {
      return "erro";
    } finally {
      setEnviando(false);
    }
  }

  async function remover(anexoId: string): Promise<ResultadoAnexo> {
    setRemovendo(true);
    try {
      await removerFichaAnexo(obraId, fichaId, anexoId);
      query.reload();
      return "ok";
    } catch {
      return "erro";
    } finally {
      setRemovendo(false);
    }
  }

  async function baixar(anexo: FichaAnexo): Promise<ResultadoAnexo> {
    try {
      const arquivo = await baixarFichaAnexo(obraId, fichaId, anexo.id);
      downloadFile(
        arquivo.content,
        arquivo.fileName ?? anexo.nomeArquivo,
        arquivo.mimeType ?? MIME_PADRAO,
      );
      return "ok";
    } catch {
      return "erro";
    }
  }

  async function abrir(anexo: FichaAnexo): Promise<ResultadoAnexo> {
    try {
      const arquivo = await baixarFichaAnexo(obraId, fichaId, anexo.id);
      const blob = new Blob([arquivo.content as BlobPart], {
        type: arquivo.mimeType ?? MIME_PADRAO,
      });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      // A aba nova já leu o blob quando o navegador resolve a navegação; libera depois.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      return "ok";
    } catch {
      return "erro";
    }
  }

  return {
    status: query.status,
    errorMessage: query.errorMessage,
    reload: query.reload,
    anexos: query.data ?? [],
    enviando,
    removendo,
    incluir,
    remover,
    baixar,
    abrir,
  };
}
